import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./banner_manager.scss";
import logo from "../../assets/fitoja-logo.jpg";
import maleProfilePic from "../../assets/profile_pic_male.jpeg";
import closeIcon from "../../assets/close_icon.png";
import deleteIcon from "../../assets/delete_icon.png";
import { toast } from "react-toastify";

interface BannerManager {
  id: string;
  navigateTo: string;
  bannerImageUrl: string;
  createdAt: Date;
}

const BannerManager = () => {
  const [userDetails, setUserDetails] = useState<any>(null);
  const [banner, setBannerManagers] = useState<BannerManager[]>([]);
  const [role, setRole] = useState<string>("");
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true); // Add loading state
  const [bannerToDelete, setBannerManagerToDelete] =
    useState<BannerManager | null>(null);
  const [bannerImageUrl, setBannerImageFile] = useState<File | null>(null); // For image upload
  const [showCreateBannerOverlay, setShowCreateBannerOverlay] =
    useState<boolean>(false);
  const [newBannerManager, setNewBannerManager] = useState({
    navigateTo: "",
  }); // New BannerManager state
  const navigate = useNavigate();
  const isInitialRender = useRef(true);

  // Fetch user details and banner
  const fetchUserDetails = async () => {
    try {
      const email = localStorage.getItem("email");
      const token = localStorage.getItem("token");
      if (!email || !token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `https://api.fitoja.in/api/get-employee-details/${email}`,
        config
      );
      setUserDetails(response.data);
      setRole(response.data.role);
      fetchAllBanners();
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      handleLogout();
    }
  };

  const fetchAllBanners = async () => {
    try {
      const response = await axios.get(
        `https://api.fitoja.in/api/get-all-banners`
      );
      setBannerManagers(response.data.data);
    } catch (error) {
      console.error("Failed to fetch banner:", error);
    } finally {
      setLoading(false); // Stop the loading indicator once API responds
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("email");
    localStorage.removeItem("token");
    navigate("/login");
  };

  const handleDelete = async () => {
    if (!bannerToDelete) return;
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(
        `https://api.fitoja.in/api/delete-banner/${bannerToDelete.id}`,
        config
      );
      fetchAllBanners();
      closeDeleteModal();
    } catch (error) {
      console.error("Failed to delete referral code:", error);
    }
  };

  const openDeleteModal = (banner: BannerManager) => {
    setBannerManagerToDelete(banner);
    setDeleteModalVisible(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalVisible(false);
    setBannerManagerToDelete(null);
  };

  // Create new Banner
  const handleCreateBanner = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBannerManager.navigateTo || !bannerImageUrl) {
      toast.error("Please fill in all the required fields!");
      return;
    }

    const formData = new FormData();
    formData.append("navigateTo", newBannerManager.navigateTo);
    formData.append("bannerImage", bannerImageUrl as File);

    try {
      const response = await axios.post(
        "https://api.fitoja.in/api/create-banner",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (response.data.success) {
        toast.success("Banner created successfully!");
        setShowCreateBannerOverlay(false);
        fetchAllBanners(); // Refresh list
      }
    } catch (error) {
      console.error("API Error:", error.response?.data || error.message);
      toast.error("Failed to create banner. Please try again.");
    }
    
  };

  useEffect(() => {
    if (isInitialRender.current) {
      fetchUserDetails();
      isInitialRender.current = false;
    }
  }, []);

  return (
    <div className="banner-manager">
      {/* Loading indicator */}
      {loading && (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading, please wait...</p>
        </div>
      )}

      {!loading && (
        <>
          <header className="dashboard-header">
            <div className="logo" onClick={() => navigate("/dashboard")}>
              <img src={logo} />
            </div>
            <div
              className="profile-section"
              onClick={() => setShowOverlay(!showOverlay)}
            >
              <img className="profile-pic" src={maleProfilePic} alt="Profile" />
            </div>
          </header>

          {showOverlay && userDetails && (
            <div className="profile-overlay">
              <div className="overlay-content">
                <img
                  className="close_icon"
                  onClick={() => setShowOverlay(false)}
                  src={closeIcon}
                />
                <p>
                  <strong>Name:</strong> {userDetails.name}
                </p>
                <p>
                  <strong>Email:</strong> {userDetails.email}
                </p>
                <p>
                  <strong>Phone Number:</strong> {userDetails.phoneNumber}
                </p>
                <p>
                  <strong>Role:</strong> {userDetails.role}
                </p>
                <p>
                  <strong>Gender:</strong> {userDetails.gender}
                </p>
                <button className="logout-button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          )}

          <h2 style={{ marginTop: "110px", marginBottom: "0px" }}>
            Banner Manager
          </h2>

          <div
            className="create-new-banner-manager"
            onClick={() => setShowCreateBannerOverlay(true)}
          >
            <span>Create New Banner</span>
          </div>

          <hr></hr>

          {showCreateBannerOverlay && (
            <div className="create-banner-overlay">
              <div className="create-banner-content">
                <h3>Create New Transformation</h3>
                <form
                  style={{ display: "grid", gap: "10px", alignItems: "start" }}
                  onSubmit={handleCreateBanner}
                >
                  <div>
                    <label>Navigate to</label>
                    <input
                      type="text"
                      value={newBannerManager.navigateTo}
                      onChange={(e) =>
                        setNewBannerManager({
                          ...newBannerManager,
                          navigateTo: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div>
                    <label>Upload Before Image</label>
                    <input
                      type="file"
                      name="bannerImage"
                      onChange={(e) =>
                        setBannerImageFile(
                          e.target.files ? e.target.files[0] : null
                        )
                      }
                      required
                    />
                  </div>
                  <br></br>
                  <button type="submit">Create Banner</button>
                  <button
                    type="button"
                    onClick={() => setShowCreateBannerOverlay(false)}
                  >
                    Cancel
                  </button>
                </form>
              </div>
            </div>
          )}

          <div className="banner-manager-list">
            {banner.length > 0 ? (
              banner.map((banner) => (
                <div key={banner.id} className="banner-manager-card">
                  <div className="banner-manager-details">
                    <img className="banner-img" src={banner?.bannerImageUrl}></img>
                    <p>
                      <strong>Navigate to:</strong> <span>{banner?.navigateTo}</span>
                    </p>
                  </div>
                  <div
                    className="delete-icon"
                    onClick={() => openDeleteModal(banner)}
                  >
                    <img className="img" src={deleteIcon}></img>
                  </div>
                </div>
              ))
            ) : (
              <p
                style={{
                  display: "flex",
                  height: "70vh",
                  alignItems: "center",
                }}
              >
                No Banner found
              </p>
            )}
          </div>

          {deleteModalVisible && (
            <div className="delete-modal">
              <div className="delete-modal-content">
                <h2>Delete Confirmation</h2>
                <p>Are you sure you want to delete this Banner?</p>
                <span style={{ display: "flex", gap: "20px" }}>
                  <button onClick={closeDeleteModal} className="cancel-button">
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="confirm-delete-button"
                  >
                    Yes, Delete
                  </button>
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BannerManager;
